import React from 'react';
import { useNavigation } from '../context/NavigationContext';
import { legalConfig, isFieldPlaceholder } from '../../config/legalConfig';
import { ShieldCheck, Scale, ArrowLeft, Calendar, Mail } from 'lucide-react';

export const KvkkPage: React.FC = () => {
  const { navigate } = useNavigation();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Link */}
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 text-xs font-semibold text-slate-400 hover:text-white transition-colors mb-8 group"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
          <span>Ana Sayfaya Dön</span>
        </button>

        {/* Header */}
        <div className="border-b border-slate-800 pb-8 mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 mb-4">
            <Scale className="w-3.5 h-3.5" />
            <span>6698 Sayılı Kanun Kapsamında Aydınlatma</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight leading-tight">
            KVKK AYDINLATMA METNİ
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-6 text-xs text-slate-400">
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-indigo-400" />
              <span>Veri Sorumlusu: <strong className="text-slate-200">{legalConfig.companyName}</strong></span>
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-indigo-400" />
              <span>Yürürlük Tarihi: <strong className="text-slate-200">{legalConfig.effectiveDate}</strong></span>
            </span>
            <span className="text-slate-500">Sürüm: {legalConfig.kvkkVersion}</span>
          </div>
          <p className="mt-4 text-sm text-slate-300 leading-relaxed">
            {legalConfig.relationshipStatement} 6698 sayılı Kişisel Verilerin Korunması Kanunu (&quot;KVKK&quot;) madde 10 uyarınca, kişisel verilerinizin işlenme amaçları, hukuki sebepleri, toplanma yöntemleri ve kanuni haklarınız hakkında sizleri bilgilendiriyoruz.
          </p>
        </div>

        {/* 12 Sections */}
        <div className="prose prose-invert prose-slate max-w-none text-sm leading-relaxed space-y-8 text-slate-300">
          {/* 1. Veri Sorumlusu */}
          <section className="bg-slate-900/50 p-6 rounded-2xl border border-slate-800">
            <h2 className="text-base font-bold text-white mb-2">1. Veri Sorumlusunun Kimliği</h2>
            <p>
              KVKK uyarınca veri sorumlusu; Adiosoft platformunu işleten <strong className="text-white">{legalConfig.companyName}</strong>&apos;dir.
            </p>
            <p className="text-xs text-slate-400 mt-2">
              Adres: {legalConfig.registeredOffice} | Vergi No: {legalConfig.taxNumber} | İletişim: {legalConfig.privacyEmail}
            </p>
          </section>

          {/* 2. İşlenen Veriler */}
          <section className="space-y-2">
            <h2 className="text-base font-bold text-white">2. İşlenen Kişisel Veriler</h2>
            <ul className="list-disc pl-5 space-y-1.5 text-slate-400">
              <li><strong className="text-slate-200">Kimlik Verisi:</strong> Ad, soyad, firma unvanı.</li>
              <li><strong className="text-slate-200">İletişim Verisi:</strong> E-posta adresi, telefon numarası, fatura adresi.</li>
              <li><strong className="text-slate-200">Müşteri İşlem Verisi:</strong> Seçilen abonelik paketi, sipariş numarası, ödeme tarihi, lisans süresi, destek talepleri.</li>
              <li><strong className="text-slate-200">İşlem Güvenliği Verisi:</strong> IP adresi, şifre hash&apos;i, oturum logları, sözleşme kabul kayıtları.</li>
              <li><strong className="text-slate-200">Pazarlama ve Hizmet Verisi:</strong> AI Local Visibility arama sorguları ve altyapı lisans talep detayları.</li>
            </ul>
          </section>

          {/* 3. Amaçlar */}
          <section className="space-y-2">
            <h2 className="text-base font-bold text-white">3. Kişisel Verilerin İşlenme Amaçları</h2>
            <p>
              Verileriniz; üyelik ve abonelik sözleşmesinin kurulması ve ifası, web sitesi oluşturma ve yayınlama araçlarının sunulması, ödemelerin muhasebeleştirilmesi, yasal yükümlülüklerin yerine getirilmesi, sistem güvenliğinin sağlanması ve müşteri destek hizmetlerinin yürütülmesi amaçlarıyla işlenir.
            </p>
          </section>

          {/* 4. Hukuki Sebepler */}
          <section className="space-y-2">
            <h2 className="text-base font-bold text-white">4. Kişisel Veri İşlemenin Hukuki Sebepleri</h2>
            <p>Kişisel verileriniz, KVKK m. 5/2 kapsamında yer alan aşağıdaki hukuki sebeplere dayanılarak işlenmektedir:</p>
            <ul className="list-disc pl-5 space-y-1.5 text-slate-400">
              <li><strong className="text-slate-200">m. 5/2-c:</strong> Kullanıcı ile akdedilen abonelik ve lisans sözleşmesinin kurulması veya ifasıyla doğrudan doğruya ilgili olması,</li>
              <li><strong className="text-slate-200">m. 5/2-ç:</strong> Şirketimizin kanuni ve vergisel yükümlülüklerini yerine getirebilmesi için veri işlemenin zorunlu olması,</li>
              <li><strong className="text-slate-200">m. 5/2-e:</strong> Bir hakkın tesisi, kullanılması veya korunması için veri işlemenin zorunlu olması,</li>
              <li><strong className="text-slate-200">m. 5/2-f:</strong> İlgili kişinin temel hak ve özgürlüklerine zarar vermemek kaydıyla, Şirketimizin meşru menfaatleri (bilgi güvenliği, hata giderme) için zorunlu olması.</li>
            </ul>
          </section>

          {/* 5. Toplama Yöntemi */}
          <section className="space-y-2">
            <h2 className="text-base font-bold text-white">5. Kişisel Verilerin Toplanma Yöntemi</h2>
            <p>
              Kişisel verileriniz; web sitemiz, kayıt formları, iletişim formları, ödeme ekranları ve sistem logları vasıtasıyla tamamen veya kısmen otomatik yollarla elektronik ortamda toplanmaktadır.
            </p>
          </section>

          {/* 6. Aktarım Alıcı Grupları & 7. Aktarım Amaçları */}
          <section className="space-y-2">
            <h2 className="text-base font-bold text-white">6. Aktarım Yapılabilecek Alıcı Grupları ve 7. Aktarım Amaçları</h2>
            <p>
              Kişisel verileriniz; yukarıda belirtilen amaçların gerçekleştirilmesi doğrultusunda ve mevzuatın çizdiği sınırlar çerçevesinde:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 text-slate-400">
              <li>Güvenli bulut altyapısı ve veri depolama sağlayıcılarına (hizmetin barındırılması ve kesintisizliği için),</li>
              <li>Yetkili ödeme kuruluşu Shopier&apos;e (ödeme güvenliği ve tahsilat işlemleri için),</li>
              <li>Gerektiğinde yetkili kamu kurum ve kuruluşları ile adli mercilere (yasal yükümlülüklerin ifası için) aktarılabilir.</li>
            </ul>
          </section>

          {/* 8. Saklama Süreleri */}
          <section className="space-y-2">
            <h2 className="text-base font-bold text-white">8. Veri Saklama Süreleri</h2>
            <p>
              Kişisel veriler, işleme amacının gerektirdiği süre boyunca ve Türk Ticaret Kanunu, Vergi Usul Kanunu ile ilgili mevzuatta öngörülen zamanaşımı ve zorunlu saklama süreleri boyunca muhafaza edilir. Sürenin dolması halinde veriler silinir veya anonim hale getirilir.
            </p>
          </section>

          {/* 9. Veri Güvenliği Tedbirleri */}
          <section className="space-y-2">
            <h2 className="text-base font-bold text-white">9. Veri Güvenliğine İlişkin Tedbirler</h2>
            <p>
              Kişisel verilerin hukuka aykırı işlenmesini ve erişilmesini önlemek amacıyla SSL/TLS şifreleme, erişim yetkilendirme sınırları, güçlü şifreleme algoritmaları ve düzenli güvenlik denetimleri dahil gerekli teknik ve idari tedbirler alınmaktadır.
            </p>
          </section>

          {/* 10. İlgili Kişinin Hakları (KVKK m. 11) */}
          <section className="space-y-2">
            <h2 className="text-base font-bold text-white">10. İlgili Kişinin KVKK m. 11 Kapsamındaki Hakları</h2>
            <p>KVKK madde 11 uyarınca her ilgili kişi Şirketimize başvurarak:</p>
            <ul className="list-disc pl-5 space-y-1 text-slate-400 text-xs">
              <li>Kişisel verilerinin işlenip işlenmediğini öğrenme,</li>
              <li>İşlenmişse buna ilişkin bilgi talep etme,</li>
              <li>İşlenme amacını ve bunların amacına uygun kullanılıp kullanılmadığını öğrenme,</li>
              <li>Yurt içinde veya yurt dışında kişisel verilerin aktarıldığı üçüncü kişileri bilme,</li>
              <li>Kişisel verilerin eksik veya yanlış işlenmiş olması hâlinde bunların düzeltilmesini isteme,</li>
              <li>KVKK m. 7&apos;deki şartlar çerçevesinde kişisel verilerin silinmesini veya yok edilmesini isteme,</li>
              <li>İşlenen verilerin münhasıran otomatik sistemler vasıtasıyla analiz edilmesi suretiyle kişinin kendisi aleyhine bir sonucun ortaya çıkmasına itiraz etme,</li>
              <li>Kişisel verilerin kanuna aykırı olarak işlenmesi sebebiyle zarara uğraması hâlinde zararın giderilmesini talep etme haklarına sahiptir.</li>
            </ul>
          </section>

          {/* 11. Başvuru Yöntemi & 12. İletişim */}
          <section className="space-y-2">
            <h2 className="text-base font-bold text-white">11. Başvuru Yöntemi ve 12. İletişim</h2>
            <p>
              Haklarınıza ilişkin taleplerinizi, Veri Sorumlusuna Başvuru Usul ve Esasları Hakkında Tebliğ&apos;e uygun olarak sistemde kayıtlı e-posta adresiniz üzerinden <strong className="text-white">{legalConfig.privacyEmail}</strong> adresine iletebilirsiniz. Başvurunuz en geç 30 (otuz) gün içinde ücretsiz olarak sonuçlandırılacaktır.
            </p>
          </section>
        </div>

        {/* Company Contact Footer */}
        <div className="mt-12 p-6 rounded-2xl bg-slate-900 border border-slate-800 text-xs text-slate-400 space-y-2">
          <p className="font-semibold text-slate-200">Veri Sorumlusu Resmi Bildirim:</p>
          <p>Unvan: <strong className="text-white">{legalConfig.companyName}</strong></p>
          <p>Adres: {legalConfig.registeredOffice}</p>
          <p>KVKK Bildirim E-Postası: {legalConfig.privacyEmail}</p>
        </div>
      </div>
    </div>
  );
};
export default KvkkPage;
